import React, { useEffect, useState, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast as sonnerToast } from 'sonner';
import { Send, Loader2, Mail, MessageSquare } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { submitMarketingLead } from '@/lib/leadCapture';
import { logFirebaseEvent } from '@/lib/tracking';

const ContactPage: React.FC = () => {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryIntent = params.get('intent')?.trim();
    const queryEmail = params.get('email')?.trim();
    if (queryIntent && !subject) setSubject(queryIntent.replace(/_/g, ' '));
    if (queryEmail && !email) setEmail(queryEmail);
  }, [location.search, subject, email]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;
    setIsSubmitting(true);

    try {
      const params = new URLSearchParams(location.search);
      await submitMarketingLead({
        source: 'contact_form',
        email: email.trim(),
        name: name.trim() || undefined,
        company: company.trim() || undefined,
        message: [subject.trim() ? `Subject: ${subject.trim()}` : undefined, message.trim()].filter(Boolean).join('\n\n'),
        intent: params.get('intent')?.trim() || undefined,
        pagePath: location.pathname,
        phone: phone.trim() || undefined,
      });

      logFirebaseEvent('marketing_lead_capture', {
        slot: 'contact_form',
        method: 'api_capture',
      });

      sonnerToast.success('Message Sent', { description: 'Thanks. We will follow up shortly with next steps.' });
      setName('');
      setCompany('');
      setMessage('');
      setPhone('');
    } catch (error: any) {
      sonnerToast.error('Could not send message', { description: error?.message || 'Please try again in a minute.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f5ef] py-20 font-sans md:py-32">
      <SEO title="Contact LendLifted" description="Talk with the LendLifted team about financing-enabled checkout for your business." canonical="/contact" />

      <div className="container mx-auto max-w-6xl px-6">
        <div className="grid items-start gap-14 lg:grid-cols-2">
          <div>
            <h1 className="mb-7 text-5xl font-extrabold text-slate-950 md:text-6xl">Get in touch</h1>
            <p className="mb-10 text-xl leading-relaxed text-slate-600">
              Whether you have a question about features, pricing, or launch support, our team will help you move faster.
            </p>

            <div className="space-y-7">
              <div className="flex items-start gap-5">
                <div className="rounded-2xl bg-slate-100 p-4 text-slate-900">
                  <MessageSquare size={24} />
                </div>
                <div>
                  <h3 className="mb-1 text-lg font-bold text-slate-900">Chat Support</h3>
                  <p className="text-slate-600">Available Mon-Fri, 9am-5pm EST.</p>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="rounded-2xl bg-slate-100 p-4 text-slate-900">
                  <Mail size={24} />
                </div>
                <div>
                  <h3 className="mb-1 text-lg font-bold text-slate-900">Email Us</h3>
                  <p className="mb-1 text-slate-600">Drop us a line anytime.</p>
                  <a href="mailto:support@lendlifted.com" className="font-medium text-slate-900 hover:underline">
                    support@lendlifted.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <Card className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="h-2 w-full bg-gradient-to-r from-emerald-400 to-indigo-400" />
            <CardContent className="p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-medium text-slate-600">Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required disabled={isSubmitting} className="h-12 border-slate-200 bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-slate-600">Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" required disabled={isSubmitting} className="h-12 border-slate-200 bg-slate-50" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-slate-600">Company</Label>
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Services" disabled={isSubmitting} className="h-12 border-slate-200 bg-slate-50" />
                </div>

                <div className="hidden" aria-hidden="true">
                  <Label className="font-medium text-slate-600">Phone</Label>
                  <Input tabIndex={-1} autoComplete="off" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Leave blank" />
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-slate-600">Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="How can we help?" required disabled={isSubmitting} className="h-12 border-slate-200 bg-slate-50" />
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-slate-600">Message</Label>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us more..." className="min-h-[160px] border-slate-200 bg-slate-50 p-4" required disabled={isSubmitting} />
                </div>

                <Button type="submit" className="h-14 w-full rounded-xl bg-emerald-600 text-lg text-white hover:bg-emerald-500" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Send className="mr-2 h-5 w-5" />}
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
